import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Calendar,
  Download,
  FileText,
  Filter,
  Search,
  Shield,
  Trash2,
  Users,
  Clock,
  Globe,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  Info
} from 'lucide-react';
import {
  fetchRetentionStatistics,
  fetchUserRetentionData,
  type UserRetentionData,
  type RetentionStatistics,
  type Jurisdiction,
  type RetentionStatus
} from '../../services/api/dataRetentionApi';
import DeletionRequestManager from './DeletionRequestManager';
import AuditLogViewer from './AuditLogViewer';
import TabNavigation, { Tab } from './components/TabNavigation';

interface DataRetentionDashboardProps {
  userRole?: string;
}

const DataRetentionDashboard: React.FC<DataRetentionDashboardProps> = ({ userRole }) => {
  const [activeView, setActiveView] = useState<'overview' | 'users' | 'calendar' | 'deletion-requests' | 'audit-log'>('overview');
  const [filterJurisdiction, setFilterJurisdiction] = useState<Jurisdiction | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<RetentionStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // React Query: Fetch retention statistics
  const { data: statisticsResponse, isLoading: isLoadingStatistics, error: statisticsError } = useQuery({
    queryKey: ['retention-statistics'],
    queryFn: fetchRetentionStatistics,
    staleTime: 60 * 1000 // Cache for 1 minute
  });

  const statistics = statisticsResponse?.data || {
    totalUsers: 0,
    activeUsers: 0,
    approachingPurgeUsers: 0,
    scheduledForPurgeUsers: 0,
    byJurisdiction: { US: 0, CA: 0, UK: 0 },
    byWarningLevel: { 90: 0, 30: 0, 7: 0 },
    pendingDeletionRequests: 0,
    upcomingPurges: []
  };

  // React Query: Fetch user retention data
  const { data: usersResponse, isLoading: isLoadingUsers, error: usersError } = useQuery({
    queryKey: ['user-retention-data'],
    queryFn: fetchUserRetentionData,
    staleTime: 60 * 1000 // Cache for 1 minute
  });

  const allUsers = usersResponse?.data || [];

  // Client-side filtering for users
  const filteredUsers = useMemo(() => {
    return allUsers.filter(user => {
      const matchesJurisdiction = filterJurisdiction === 'all' || user.jurisdiction === filterJurisdiction;
      const matchesStatus = filterStatus === 'all' || user.retentionStatus === filterStatus;
      return matchesJurisdiction && matchesStatus;
    });
  }, [allUsers, filterJurisdiction, filterStatus]);

  const getStatusColor = (status: RetentionStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      case 'approaching_purge':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
      case 'scheduled_for_purge':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getJurisdictionFlag = (jurisdiction: Jurisdiction) => {
    switch (jurisdiction) {
      case 'US': return '🇺🇸';
      case 'CA': return '🇨🇦';
      case 'UK': return '🇬🇧';
      default: return '🌍';
    }
  };

  const renderOverview = () => {
    // Loading state
    if (isLoadingStatistics) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-center" style={{ padding: '3rem' }}>
            <div className="text-center">
              <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
              <p style={{ color: 'var(--text-secondary)' }}>Loading retention statistics...</p>
            </div>
          </div>
        </div>
      );
    }

    // Error state
    if (statisticsError) {
      return (
        <div className="space-y-6">
          <div className="alert alert-error" style={{ padding: '1rem', background: '#FEE2E2', border: '1px solid #DC2626', borderRadius: '8px' }}>
            <p style={{ color: '#DC2626' }}>Failed to load retention statistics. Please try again.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Developer indicator */}
        {process.env.NODE_ENV === 'development' && statistics.totalUsers > 0 && (
          <div style={{
            padding: '0.75rem 1rem',
            background: '#E3F2FD',
            borderRadius: '6px',
            fontSize: '0.875rem',
            color: '#1565C0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            border: '1px solid #90CAF9'
          }}>
            <Info className="h-4 w-4" />
            <span>
              <strong>Development Mode:</strong> Showing {statistics.totalUsers.toLocaleString()} users from mock data.
              Connect backend API to see real-time data.
            </span>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Users</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                {statistics.totalUsers.toLocaleString()}
              </p>
            </div>
            <Users className="h-12 w-12 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Approaching Purge</p>
              <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mt-2">
                {statistics.approachingPurgeUsers}
              </p>
            </div>
            <AlertTriangle className="h-12 w-12 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Scheduled for Purge</p>
              <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">
                {statistics.scheduledForPurgeUsers}
              </p>
            </div>
            <Trash2 className="h-12 w-12 text-red-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Deletion Requests</p>
              <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mt-2">
                {statistics.pendingDeletionRequests}
              </p>
            </div>
            <FileText className="h-12 w-12 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Warning Level Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Warning Level Distribution
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">90 Days Warning</p>
            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-2">
              {statistics.byWarningLevel[90]}
            </p>
          </div>
          <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">30 Days Warning</p>
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-2">
              {statistics.byWarningLevel[30]}
            </p>
          </div>
          <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">7 Days Warning</p>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-2">
              {statistics.byWarningLevel[7]}
            </p>
          </div>
        </div>
      </div>

      {/* Jurisdiction Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Users by Jurisdiction
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">🇺🇸</span>
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">United States</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">CCPA Compliance</p>
              </div>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {statistics.byJurisdiction.US}
            </p>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">🇨🇦</span>
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Canada</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">PIPEDA & Law 25</p>
              </div>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {statistics.byJurisdiction.CA}
            </p>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">🇬🇧</span>
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">United Kingdom</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">UK GDPR</p>
              </div>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {statistics.byJurisdiction.UK}
            </p>
          </div>
        </div>
      </div>
      </div>
    );
  };

  const renderUsersTable = () => {
    // Loading state
    if (isLoadingUsers) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-center" style={{ padding: '3rem' }}>
            <div className="text-center">
              <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
              <p style={{ color: 'var(--text-secondary)' }}>Loading user retention data...</p>
            </div>
          </div>
        </div>
      );
    }

    // Error state
    if (usersError) {
      return (
        <div className="space-y-4">
          <div className="alert alert-error" style={{ padding: '1rem', background: '#FEE2E2', border: '1px solid #DC2626', borderRadius: '8px' }}>
            <p style={{ color: '#DC2626' }}>Failed to load user retention data. Please try again.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Developer indicator */}
        {process.env.NODE_ENV === 'development' && filteredUsers.length > 0 && (
          <div style={{
            padding: '0.75rem 1rem',
            background: '#E3F2FD',
            borderRadius: '6px',
            fontSize: '0.875rem',
            color: '#1565C0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            border: '1px solid #90CAF9'
          }}>
            <Info className="h-4 w-4" />
            <span>
              <strong>Development Mode:</strong> Showing {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} from mock data.
              Connect backend API to see real-time data.
            </span>
          </div>
        )}
        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <select
            value={filterJurisdiction}
            onChange={(e) => setFilterJurisdiction(e.target.value as Jurisdiction | 'all')}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Jurisdictions</option>
            <option value="US">🇺🇸 United States</option>
            <option value="CA">🇨🇦 Canada</option>
            <option value="UK">🇬🇧 United Kingdom</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as RetentionStatus | 'all')}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="approaching_purge">Approaching Purge</option>
            <option value="scheduled_for_purge">Scheduled for Purge</option>
          </select>
        </div>

        {/* Users Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Jurisdiction</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Last Activity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Days Until Purge</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.map((user) => (
                <tr key={user.userId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    User #{user.userId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <span className="text-lg mr-2">{getJurisdictionFlag(user.jurisdiction)}</span>
                    {user.jurisdiction}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {user.lastActivityDate.toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(user.retentionStatus)}`}>
                      {user.retentionStatus.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {user.daysUntilPurge !== undefined ? (
                      <span className={user.daysUntilPurge <= 7 ? 'text-red-600 dark:text-red-400 font-bold' : 'text-gray-900 dark:text-gray-100'}>
                        {user.daysUntilPurge} days
                      </span>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                    <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                      View
                    </button>
                    <button className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300">
                      Export
                    </button>
                    <button className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300">
                      Purge
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const tabs: Tab[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users', badge: statistics.totalUsers },
    { id: 'deletion-requests', label: 'Deletion Requests', badge: statistics.pendingDeletionRequests },
    { id: 'audit-log', label: 'Audit Log' },
    { id: 'calendar', label: 'Calendar' }
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm text-gray-600 dark:text-gray-400" aria-label="Breadcrumb">
        <span>Setup</span>
        <ChevronRight className="h-4 w-4 mx-2" />
        <span className="text-gray-900 dark:text-gray-100 font-medium">Data Retention</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Data Retention Management</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage user data retention and purging for GDPR/CCPA/PIPEDA compliance
          </p>
        </div>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center">
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </button>
      </div>

      {/* View Tabs */}
      <TabNavigation
        tabs={tabs}
        activeTab={activeView}
        onTabChange={(tabId) => setActiveView(tabId as any)}
      />

      {/* Content */}
      {activeView === 'overview' && renderOverview()}
      {activeView === 'users' && renderUsersTable()}
      {activeView === 'deletion-requests' && <DeletionRequestManager userRole={userRole as any} />}
      {activeView === 'audit-log' && <AuditLogViewer userRole={userRole as any} />}
      {activeView === 'calendar' && (
        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
          <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-400" />
          <p>Calendar view coming soon...</p>
        </div>
      )}
    </div>
  );
};

export default DataRetentionDashboard;
